import { useAtom, useAtomValue } from 'jotai';

import {
  campaignEditorSubmitPayloadAtom,
  campaignEndDateAtom,
  campaignNameAtom,
  campaignStartDateAtom,
  creatorItemsAtom,
  creatorKeywordsAtom,
  draftCreatorItemsAtom,
  draftPostItemsAtom,
  hasEditedCampaignNameAtom,
  hasEndDateAtom,
  postItemsAtom,
  trackedItemSummaryAtom,
} from '../store/campaign-editor-atoms';
import type { CampaignEditorDraft } from '../types';

export function useCampaignBasicsDraft(): CampaignEditorDraft['basics'] {
  const [campaignEndDate, setCampaignEndDate] = useAtom(campaignEndDateAtom);
  const [campaignName, setCampaignName] = useAtom(campaignNameAtom);
  const [campaignStartDate, setCampaignStartDate] = useAtom(campaignStartDateAtom);
  const [hasEditedCampaignName, setHasEditedCampaignName] = useAtom(hasEditedCampaignNameAtom);
  const [hasEndDate, setHasEndDate] = useAtom(hasEndDateAtom);

  return {
    campaignEndDate,
    campaignName,
    campaignStartDate,
    hasEditedCampaignName,
    hasEndDate,
    setCampaignEndDate,
    setCampaignName,
    setCampaignStartDate,
    setHasEditedCampaignName,
    setHasEndDate,
  };
}

export function useCampaignCreatorsDraft(): CampaignEditorDraft['creators'] {
  const [creatorItems, setCreatorItems] = useAtom(creatorItemsAtom);
  const [creatorKeywords, setCreatorKeywords] = useAtom(creatorKeywordsAtom);

  return {
    creatorItems,
    creatorKeywords,
    setCreatorItems,
    setCreatorKeywords,
  };
}

export function useCampaignPostsDraft(): CampaignEditorDraft['posts'] {
  const [postItems, setPostItems] = useAtom(postItemsAtom);

  return {
    postItems,
    setPostItems,
  };
}

export function useCampaignEditorDraft(campaignItemSlotLimit: number): CampaignEditorDraft {
  const basics = useCampaignBasicsDraft();
  const creators = useCampaignCreatorsDraft();
  const posts = useCampaignPostsDraft();
  const draftCreatorItems = useAtomValue(draftCreatorItemsAtom);
  const draftPostItems = useAtomValue(draftPostItemsAtom);
  const summary = useAtomValue(trackedItemSummaryAtom);
  const serialize = useAtomValue(campaignEditorSubmitPayloadAtom);

  return {
    basics,
    creators,
    posts,
    summary: {
      ...summary,
      hasCreators: draftCreatorItems.length > 0,
      hasPostUrls: draftPostItems.length > 0,
      remainingSlots: Math.max(
        campaignItemSlotLimit - draftCreatorItems.length - draftPostItems.length,
        0,
      ),
      totalTrackedItems: draftCreatorItems.length + draftPostItems.length,
    },
    serialize: () => serialize,
  };
}
